import { cloneElement, useCallback, useEffect, useMemo, useState } from 'react'
import { Body, TruncateText } from '@panwds/react-ui'
import { CURRENT_TENANT } from '../../subscription/components/constants/SubscriptionConstant'
import {
  APP_ID_LOGGING_SERVICE,
  APP_ID_SDWAN,
  CREATE_NEW,
  KEYS,
} from './common/GlobalConstants'
import { CONTEXT_TO_APP_ID } from '../constants'
import type { VFC } from 'react'
import type { CommonStoreState, ContextApp } from '../store'
import type { IDropDownData } from './common/DropDown/DropDown'
import type { ScreenProps } from '../renderer'
import { updateStateProps } from '../actions'
import { useFormContext } from 'react-hook-form'
import { isNonemptyArray } from './common/utils'

export type DefaultActivationCommonDropdownsScreenProps = ScreenProps & { state: CommonStoreState }
const DefaultActivationCommonDropdowns: VFC<DefaultActivationCommonDropdownsScreenProps> = (props) => {
  const { state, children } = props
  const { tsg, loadingTsg, instances, isHashFullyActivated, licenses, manifest, lockedInfo, context } = state
  const [selectedSupportAccount, setSelectedSupportAccount] = useState<IDropDownData>({ value: '', id: '' })
  const [disableTsgDropDowns, setDisableTsgDropDowns] = useState<boolean>(false)
  const [disableRegionDropDowns, setDisableRegionDropDowns] = useState<boolean>(false)
  const [disableCspDropDowns, setDisableCspDropDowns] = useState<boolean>(!!lockedInfo?.csp_id)
  const [instance, setInstance] = useState<any>()
  const [selectedTsg, setSelectedTsg] = useState<IDropDownData>({ id: '', value: '' })
  const { setValue } = useFormContext()

  const ContextToAppId: ContextApp = CONTEXT_TO_APP_ID
  /**
   *  TODO : Update TSG Onchange function based on the requirement
   */
  const onTSGChange = useCallback((value: IDropDownData) => {
    setSelectedTsg(value)
    const tsg_id = value.id === CREATE_NEW ? '' : (value.id ?? '')
    updateStateProps({ tsg: state?.tsgs?.get(tsg_id) })
    //setInstance(null)
  }, [state?.tsgs])


  useEffect(() => {
    if (tsg && !loadingTsg && isNonemptyArray(instances)) {
      const _instance = instances.find(each => (each.platform_region || each.app_id === APP_ID_LOGGING_SERVICE) && each.app_id !== APP_ID_SDWAN)
      setInstance(_instance)
      if (_instance) {
        setSelectedSupportAccount({ id: _instance.support_account_id, value: _instance.support_account_id })
      }
    }
    else if (loadingTsg || !isNonemptyArray(instances)) {
      setInstance(null)
    }
  }, [instances, loadingTsg, tsg])

  useEffect(() => {
    if (isHashFullyActivated) {
      setDisableTsgDropDowns(true)
    }
    else {
      setDisableTsgDropDowns(false)
    }
  }, [instances, instances?.length, loadingTsg, setValue, tsg, isHashFullyActivated, lockedInfo])


  useEffect(() => {
    if (!Boolean(selectedTsg?.id) || Boolean(lockedInfo?.csp_id) || isHashFullyActivated || (tsg && !loadingTsg && isNonemptyArray(instances))) {
      setDisableCspDropDowns(true)
    }
    else {
      setDisableCspDropDowns(false)
    }
  }, [instances, isHashFullyActivated, loadingTsg, lockedInfo?.csp_id, selectedTsg?.id, tsg])

  useEffect(() => {
    // only for create new or empty TSG we should allow region selection
    if (selectedTsg?.id === CREATE_NEW || (Boolean(selectedTsg?.id) && !isNonemptyArray(instances))) {
      setDisableRegionDropDowns(false)
    }
    else {
      setDisableRegionDropDowns(true)   // till TSG and CSP are selected, disable region dropdown
    }
  }, [instance, instances, lockedInfo, selectedSupportAccount, selectedTsg?.id])

  /**
   *  To show the currently  viewing in the tsg dropdown
   */
  const getCurrentView = useCallback((item: any) => {
    return (
      <div
        className='tw-flex tw-flex-col'>
        {item.id === state?.globalTsgId && <div><Body addClassName='current_tenant' appearance='secondary' >{CURRENT_TENANT}</Body></div>}
        <TruncateText>{item.description} <small className='cm-opacity-50'>- {item.id}</small></TruncateText>
      </div>
    )
  }, [state?.globalTsgId])

  const onCspChange = useCallback((value: IDropDownData) => {
    setSelectedSupportAccount(value)
  }, [])

  const tsgProps = useMemo(() => {
    const callbacks = {
      formatOptionsCallback: getCurrentView,
      changeHandlerCallback: onTSGChange,
      //customValidationCallback: customValidator
    }
    return {
      callbacks,
      allTsgList: state.tsgs,
      currentTsg: selectedTsg,
      disable: disableTsgDropDowns,
      customer_support_account: selectedSupportAccount?.id,
      dropDownList: [],
      instances,
      primaryAppId: manifest?.primary_app_id,
      primaryApp: manifest?.primary_app_id ? state?.apps.get(manifest?.primary_app_id) : null,
      lockedInfo,

    }
  }, [getCurrentView, onTSGChange, state.tsgs, selectedTsg, selectedSupportAccount?.id, disableTsgDropDowns, manifest, instances, state.apps, lockedInfo])

  const cspProps = useMemo(() => {
    const callbacks = {
      changeHandlerCallback: onCspChange,
    }
    const curTsgInstCsp = instance?.support_account_id
    return {
      callbacks,
      supportAccounts: state.supportAccounts,
      isLoading: loadingTsg,
      disable: disableCspDropDowns,
      curTsgInstCsp,
      currentTsg: selectedTsg.id,
      tsg: state.tsg,
      lockedInfo,
    }
  }, [disableCspDropDowns, instance, loadingTsg, onCspChange, state.supportAccounts, state.tsg, selectedTsg, lockedInfo])

  const regionProps = useMemo(() => {
    const callbacks = {}
    const _appIdList: string[] = licenses?.map(each => each.app_id) || (context && [ContextToAppId[context]]) || []

    return {
      callbacks,
      apps: state.apps,
      support_account_id: selectedSupportAccount?.id,
      dropDownList: [],
      appIdList: _appIdList,
      isLoading: loadingTsg,
      disable: disableRegionDropDowns,
      currentTsg: selectedTsg,
      instance: instance
    }
  }, [licenses, context, state.apps, selectedSupportAccount?.id, loadingTsg, disableRegionDropDowns, selectedTsg, instance, ContextToAppId])

  /**
   * Return dynamic properties based on the dropdown fieldname
   */
  const dynamicProps = useMemo(() => {
    return {
      [KEYS['TSG']]: tsgProps,
      [KEYS['CSP']]: cspProps,
      [KEYS['REGION']]: regionProps,
    }
  }, [cspProps, regionProps, tsgProps])

  /**
   *  Render the multiple child and override dynamic properties
   */
  const renderChildInformation = useCallback((children: any) => {
    return children?.props?.children.map((child: any) => {
      return cloneElement(child, {
        ...{
          ...child.props, ...dynamicProps[child.key], state
        }
      })
    })
  }, [dynamicProps, state])

  return <div>
    <div className='cm-flex cm-flex-row cm-space-x-2 cm-mb-2 cm-w-100 cm-w-full'>
      {renderChildInformation(children)}
    </div>
  </div>
}

export default DefaultActivationCommonDropdowns
